import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useConsumerAuth } from 'context/ConsumerAuthProvider';
import { useTheme } from 'context/ThemeProvider';
import {
  LayoutDashboard,
  Gamepad2,
  Users,
  Swords,
  Receipt,
  LogOut,
  Menu,
  Building2,
  Lock,
  Sun,
  Moon,
  type LucideIcon,
} from 'lucide-react';

interface NavItem {
  name: string;
  path: string;
  icon: LucideIcon;
  permission: string;
}

const ConsumerLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { consumer, logout, hasPermission } = useConsumerAuth();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems: NavItem[] = [
    { name: 'Dashboard', path: '/consumer/dashboard', icon: LayoutDashboard, permission: 'dashboard' },
    { name: 'Games', path: '/consumer/games', icon: Gamepad2, permission: 'games' },
    { name: 'Players', path: '/consumer/users', icon: Users, permission: 'users' },
    { name: 'Battles', path: '/consumer/battles', icon: Swords, permission: 'battles' },
    { name: 'Transactions', path: '/consumer/transactions', icon: Receipt, permission: 'transactions' },
    { name: 'Private Tables', path: '/consumer/private-tables', icon: Lock, permission: 'private-tables' },
  ].filter((item) => hasPermission(item.permission));

  const handleLogout = () => {
    logout();
    navigate('/consumer/login', { replace: true });
  };

  const pageTitle = navItems.find((item) => location.pathname.startsWith(item.path))?.name || 'Consumer Portal';
  const initial = (consumer?.consumerName || 'C').charAt(0).toUpperCase();

  const navLinks = (onNavigate?: () => void) => (
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {navItems.map((item) => {
        const active = location.pathname.startsWith(item.path);
        const Icon = item.icon;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
              active
                ? 'bg-indigo-500/10 text-indigo-700 border border-indigo-500/20'
                : 'text-muted hover:bg-subtle hover:text-heading'
            }`}
          >
            <Icon size={18} />
            {item.name}
          </Link>
        );
      })}
      {navItems.length === 0 && (
        <p className="px-3 py-2 text-xs text-muted">No modules assigned to this consumer.</p>
      )}
    </nav>
  );

  const sidebarFooter = (
    <div className="p-3 border-t border-line">
      <div className="flex items-center gap-3 px-3 py-2 mb-2">
        <div className="h-9 w-9 rounded-full bg-indigo-500/15 text-indigo-700 flex items-center justify-center font-bold text-sm">
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-heading truncate">{consumer?.consumerName}</p>
          <p className="text-[10px] text-muted truncate">Read-only access</p>
        </div>
      </div>
      <button
        type="button"
        onClick={handleLogout}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-line text-sm font-semibold text-muted hover:text-brand-danger hover:bg-red-500/5 transition-colors"
      >
        <LogOut size={16} /> Sign out
      </button>
    </div>
  );

  return (
    <div className="flex h-dvh min-h-dvh w-full flex-col overflow-hidden bg-app">
      <header className="h-16 shrink-0 bg-card border-b border-line flex items-center z-20">
        <div className="hidden lg:flex items-center gap-3 h-full w-64 shrink-0 px-5 border-r border-line">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white">
            <Building2 size={20} />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-heading truncate">Consumer Portal</p>
            <p className="text-[10px] font-mono text-brand-gold truncate">{consumer?.consumerId}</p>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-between gap-3 min-w-0 px-4 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              className="lg:hidden p-2 rounded-lg border border-line text-muted"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={18} />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-heading truncate">{pageTitle}</h1>
              <p className="text-xs text-muted hidden sm:block truncate">{consumer?.consumerName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={toggle}
              className="p-2 rounded-lg border border-line text-muted hover:text-heading"
              title="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-subtle border border-line text-xs font-mono text-brand-gold">
              {consumer?.consumerId}
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside className="hidden lg:flex lg:flex-col w-64 shrink-0 bg-card border-r border-line">
          {navLinks()}
          {sidebarFooter}
        </aside>

        {mobileOpen && (
          <div className="lg:hidden fixed inset-0 z-40">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
            <div className="absolute inset-y-0 left-0 w-64 pt-16 bg-card border-r border-line flex flex-col">
              {navLinks(() => setMobileOpen(false))}
              {sidebarFooter}
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 overflow-auto p-4 sm:p-6 bg-app">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ConsumerLayout;
